/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { hideGlobalSpinner } from '@Containers/GlobalSpinner/GlobalSpinnerActions';
import { spinnerTypes } from '@Containers/GlobalSpinner/GlobalSpinnerConstants';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import AppStorage from '@Services/AppStorage';
import HistoryHelper from '@Services/HistoryHelper';
import { historyReducerKey, typeOfHistorySaveDialog, typeOfHistoryConfirmationDialog } from '../../Constants';
import { getDateGroupLabel, getHistoryDisplayTime, getSavedHistoryTime, getHistorySelectionKey } from '../../utils';
import {
  fetchSavedHistory,
  fetchRecentHistory,
  handleViewDeleteModal,
  onDeleteHistoryRecord,
  onSaveUnLoad,
  onPageChange,
  handleSearchInput,
  openSaveModal,
} from '../../HistoryAction';
import { trackHistoryMiniSearchResultsUpdated } from '../../analytics';

export default function useHistoryData(page, isAnonymous = false) {
  const dispatch = useDispatch();
  const lastSavedSearchSignatureRef = useRef('');
  const shouldResetRecentViewRef = useRef(null);
  const previousPageRef = useRef(page);
  // Tracks the anon->named transition. Modal login (HistoryBanner) upgrades the session without
  // remounting History, so the mount-time reset flag above never re-reads. Re-arm the reset when
  // the user stops being anonymous so the preserved recent rows get merged in on the next fetch.
  const wasAnonymousRef = useRef(isAnonymous);

  const {
    savedHistory,
    historyTypes,
    isSavedHistoryLoaded,
    showSaveModal,
    showDeleteModal,
    itemTobeDeleted,
    isDeleteInProgress,
    recentHistory,
    isRecentHistoryLoaded,
    currentPage,
    pageSize,
    recentTotalElements,
    selectedItems,
    searchInputText,
  } = useSelector((state) => state.get(historyReducerKey));

  const isSaved = page === 'saved';
  const isLoaded = isSaved ? isSavedHistoryLoaded : isRecentHistoryLoaded;
  const historyData = isSaved ? savedHistory : recentHistory;

  useEffect(() => {
    if (isAnonymous) return;

    const scheduledHistory = AppStorage.instance.get('scheduledHistory');
    if (!scheduledHistory) return;

    AppStorage.instance.set('scheduledHistory', null);
    dispatch(openSaveModal({ historyItem: scheduledHistory }));
  }, [dispatch, isAnonymous]);

  // Defensive: ensure currentPage and pageSize are numbers
  const safeCurrentPage = Number.isFinite(Number(currentPage)) && Number(currentPage) > 0 ? Number(currentPage) : 1;
  const safePageSize = Number.isFinite(Number(pageSize)) && Number(pageSize) > 0 ? Number(pageSize) : 15;

  // Recent history is server-paginated, so page/size/search changes must refetch.
  useEffect(() => {
    const previousPage = previousPageRef.current;
    previousPageRef.current = page;

    if (page !== 'recent') {
      return undefined;
    }

    // The reducer stores one pagination cursor for both History tabs. Reset it when entering
    // Recent History from a later Saved History page before fetching.
    const changedToRecent = previousPage !== page;
    if (changedToRecent && safeCurrentPage !== 1) {
      dispatch(onPageChange(safePageSize, 1));
      return undefined;
    }

    if (shouldResetRecentViewRef.current === null) {
      shouldResetRecentViewRef.current = HistoryHelper.consumeAnonymousRecentHistoryReset();
    }

    const justUpgradedFromAnonymous = wasAnonymousRef.current && !isAnonymous;
    wasAnonymousRef.current = isAnonymous;

    if (shouldResetRecentViewRef.current || justUpgradedFromAnonymous) {
      shouldResetRecentViewRef.current = false;

      const needsSearchReset = Boolean(searchInputText);
      const needsPageReset = safeCurrentPage !== 1;
      if (needsSearchReset) {
        dispatch(handleSearchInput(''));
      }
      if (needsPageReset) {
        dispatch(onPageChange(safePageSize, 1));
      }

      // Resetting search/page mutates this effect's deps, so React re-runs it (else-branch) and
      // fetches once. Fetch here only when nothing was reset - firing a manual fetch alongside the
      // re-run's fetch races two page-0 requests, and whichever resolves first clears the anon
      // upgrade snapshot before the loser reads it, dropping the preserved rows from the view.
      if (!needsSearchReset && !needsPageReset) {
        dispatch(fetchRecentHistory(0, safePageSize, 'desc', ''));
      }
    } else {
      dispatch(fetchRecentHistory(safeCurrentPage - 1, safePageSize, 'desc', searchInputText)); // Convert to 0-based index for API
    }

    return () => {
      dispatch(hideGlobalSpinner());
      dispatch(onSaveUnLoad(spinnerTypes.progress));
      dispatch(closeDialog({ id: typeOfHistoryConfirmationDialog }));
      dispatch(closeDialog({ id: typeOfHistorySaveDialog }));
    };
  }, [page, safeCurrentPage, safePageSize, searchInputText, isAnonymous]);

  useEffect(
    () => () => {
      dispatch(onPageChange(safePageSize, 1));
    },
    []
  );

  // Saved history is fetched once per tab entry; pagination/filtering is client-side.
  useEffect(() => {
    if (page !== 'saved') {
      return undefined;
    }

    dispatch(fetchSavedHistory());

    return () => {
      dispatch(hideGlobalSpinner());
      dispatch(onSaveUnLoad(spinnerTypes.progress));
      dispatch(closeDialog({ id: typeOfHistoryConfirmationDialog }));
      dispatch(closeDialog({ id: typeOfHistorySaveDialog }));
    };
  }, [page]);

  // Handle delete modal
  useEffect(() => {
    if (showDeleteModal) {
      dispatch(
        updateDialog({
          id: typeOfHistoryConfirmationDialog,
          dialogType: typeOfHistoryConfirmationDialog,
          title: 'Delete',
          text: 'Are you sure?',
          submitButtonText: 'Yes',
          dismissButtonText: 'Cancel',
          isCancelButton: true,
          backdrop: false,
          onClose: () => {
            dispatch(handleViewDeleteModal(false));
          },
          onConfirm: () => {
            const payload =
              page === 'recent' ? [itemTobeDeleted] : [{ resultName: itemTobeDeleted.resultName, databaseId: itemTobeDeleted.databaseId }];
            dispatch(onDeleteHistoryRecord(payload, page));
          },
        })
      );
    } else {
      dispatch(closeDialog({ id: typeOfHistoryConfirmationDialog }));
    }
  }, [showDeleteModal]);

  // Handle save modal
  useEffect(() => {
    if (showSaveModal) {
      dispatch(
        updateDialog({
          id: typeOfHistorySaveDialog,
          dialogType: typeOfHistorySaveDialog,
        })
      );
    } else {
      dispatch(closeDialog({ id: typeOfHistorySaveDialog }));
    }
  }, [showSaveModal]);

  const handlePageChange = (newPageSize, newCurrentPage) => {
    dispatch(onPageChange(newPageSize, newCurrentPage));
  };

  const startIndex = (safeCurrentPage - 1) * safePageSize;

  const allItems = useMemo(() => {
    if (page === 'recent') {
      return [...historyData]
        .sort((x, y) => (getHistoryDisplayTime(y) || 0) - (getHistoryDisplayTime(x) || 0))
        .map((item, itemIndex) => ({
          item,
          groupItemIndex: itemIndex,
          itemIndex,
          groupItem: { databaseName: item?.databaseName },
        }));
    }

    return historyData.flatMap((groupItem, groupItemIndex) =>
      (groupItem.items || [])
        .sort((x, y) => (getSavedHistoryTime(y) || 0) - (getSavedHistoryTime(x) || 0))
        .map((item, itemIndex) => ({
          item,
          groupItemIndex,
          itemIndex,
          groupItem,
        }))
    );
  }, [historyData, page]);

  const normalizedSearch = (searchInputText || '').toLowerCase();

  const filteredItems = useMemo(() => {
    // For recent history, search is handled at API level, so return all items
    // For saved history, continue with client-side filtering
    if (page === 'recent') {
      return allItems;
    }

    return allItems.filter(({ item }) => {
      const comment = (item.comment || '').toLowerCase();
      const searchName = (item.searchName || '').toLowerCase();
      return !normalizedSearch || comment.includes(normalizedSearch) || searchName.includes(normalizedSearch);
    });
  }, [allItems, normalizedSearch, page]);

  const totalSavedPages = page === 'saved' ? Math.ceil(filteredItems.length / safePageSize) : 0;

  const computeEffectiveCurrentPage = () => {
    if (page !== 'saved') {
      return safeCurrentPage;
    }
    if (filteredItems.length === 0) {
      return 0;
    }
    return Math.min(safeCurrentPage, Math.max(totalSavedPages, 1));
  };

  const effectiveCurrentPage = computeEffectiveCurrentPage();

  // For recent: data is already paginated server-side, use all items directly.
  // For saved: apply client-side slicing.
  const savedStartIndex = page === 'saved' ? Math.max(effectiveCurrentPage - 1, 0) * safePageSize : startIndex;
  const savedEndIndex = savedStartIndex + safePageSize;
  const paginatedItems = page === 'recent' ? allItems : filteredItems.slice(savedStartIndex, savedEndIndex);
  const allSelectedOnPage =
    paginatedItems.length > 0 && paginatedItems.every(({ item }) => selectedItems.includes(getHistorySelectionKey(item)));

  const groupedPaginatedItems = useMemo(
    () =>
      paginatedItems.reduce((acc, itemData) => {
        const dbName = itemData.groupItem.databaseName;
        if (!acc[dbName]) {
          acc[dbName] = [];
        }
        acc[dbName].push(itemData);
        return acc;
      }, {}),
    [paginatedItems]
  );

  const dateGroupedPaginatedItems = useMemo(
    () =>
      paginatedItems.reduce((acc, itemData) => {
        const dateLabel = getDateGroupLabel(getHistoryDisplayTime(itemData.item) ?? itemData.item?.creationTime ?? itemData.item?.userTime);
        if (!acc[dateLabel]) {
          acc[dateLabel] = [];
        }
        acc[dateLabel].push(itemData);
        return acc;
      }, {}),
    [paginatedItems]
  );

  useEffect(() => {
    if (page !== 'saved') {
      return;
    }

    const query = (searchInputText || '').trim();
    if (!query) {
      lastSavedSearchSignatureRef.current = '';
      return;
    }

    const signature = `${query}|${filteredItems.length}|${safeCurrentPage}|${safePageSize}`;
    if (lastSavedSearchSignatureRef.current === signature) {
      return;
    }

    trackHistoryMiniSearchResultsUpdated({
      page: 'saved',
      criteria: `qs=${query}`,
      totalResults: filteredItems.length,
      currentPage: effectiveCurrentPage,
      resultsPerPage: safePageSize,
      sortType: 'Relevance-desc',
      context: filteredItems?.[0]?.item?.context,
      facets: [],
    });
    lastSavedSearchSignatureRef.current = signature;
  }, [page, searchInputText, filteredItems.length, effectiveCurrentPage, safeCurrentPage, safePageSize]);

  return {
    historyTypes,
    isLoaded,
    isDeleteInProgress,
    currentPage: effectiveCurrentPage,
    pageSize,
    totalItems: page === 'recent' ? recentTotalElements : filteredItems.length,
    selectedItems,
    searchInputText,
    paginatedItems,
    allSelectedOnPage,
    groupedPaginatedItems,
    dateGroupedPaginatedItems,
    handlePageChange,
  };
}
